import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { build, decks } from '@/lib/api';
import { toast } from '@/lib/toast';
import { BRACKET_COLORS, BRACKET_LABELS } from '@/lib/constants';
import { BRACKET_BLURBS } from '@/lib/buildRoles';
import { CardAutocomplete } from '@/components/CardAutocomplete';

interface GuidedDeckTabProps {
  /** Called with the new deck id after the commander, bracket and themes are saved. */
  onCreated: (deckId: string) => void;
  onClose: () => void;
}

const BRACKETS = [1, 2, 3, 4, 5] as const;

/** Add-deck "Guided" tab (spec §8): commander → target bracket → EDHREC themes → empty deck. */
export function GuidedDeckTab({ onCreated, onClose }: GuidedDeckTabProps) {
  const [draft, setDraft] = useState('');
  const [commander, setCommander] = useState('');
  const [deckName, setDeckName] = useState('');
  const [bracket, setBracket] = useState<number>(2);
  const [themes, setThemes] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const themesQuery = useQuery({
    queryKey: ['commanderThemes', commander],
    queryFn: () => build.commanderThemes(commander),
    enabled: commander.length > 0,
  });

  const chooseCommander = () => {
    const name = draft.trim();
    if (!name) return;
    setCommander(name);
    if (!deckName.trim()) setDeckName(name);
    setThemes(new Set());
    setError('');
  };

  const toggleTheme = (slug: string) => {
    setThemes((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const create = async () => {
    if (!commander) return;
    setCreating(true);
    setError('');
    try {
      const canonical = themesQuery.data?.commander ?? commander;
      const deck = await decks.addManual(deckName.trim() || canonical, 'commander', `1 ${canonical}`, canonical, 'guided');
      try {
        await build.setBracket(deck.id, 'base', bracket);
        await build.setTemplate(deck.id, 'base', { themes: [...themes], slot_overrides: {} });
        toast.success('Deck created — the Build tab is ready.');
      } catch {
        toast.warn('Deck created, but the bracket or themes could not be saved — set them from the Build tab.');
      }
      onCreated(deck.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create the deck');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Step 1 — commander */}
      <section>
        <div className="text-base text-on-surface-variant mb-0.5">1. Commander</div>
        <div className="flex gap-2 items-start">
          <div className="flex-1">
            <CardAutocomplete
              value={draft}
              onChange={setDraft}
              placeholder="Commander name..."
              tabComplete={true}
              commanderOnly={true}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); chooseCommander(); } }}
            />
          </div>
          <button type="button" className="btn-ghost" onClick={chooseCommander} disabled={!draft.trim()}>
            Use this commander
          </button>
        </div>
        {commander && (
          <div className="mt-1 text-sm text-on-surface-variant">
            Commander: <span className="text-on-surface font-medium">{themesQuery.data?.commander ?? commander}</span>
          </div>
        )}
      </section>

      {/* Step 2 — target bracket */}
      <section>
        <div className="text-base text-on-surface-variant mb-0.5">2. Target bracket <span className="opacity-70">(a starting point — change it any time from the Build tab)</span></div>
        <div role="radiogroup" className="flex gap-1.5 flex-wrap">
          {BRACKETS.map((b) => (
            <button
              key={b}
              type="button"
              role="radio"
              aria-checked={bracket === b}
              aria-label={`Bracket ${b}`}
              onClick={() => setBracket(b)}
              className="flex-1 min-w-[96px] rounded-sm border px-2 py-1.5 text-left cursor-pointer"
              style={{
                borderColor: bracket === b ? BRACKET_COLORS[b] : 'var(--outline-variant)',
                background: bracket === b ? 'var(--surface-container-high)' : 'var(--surface-container)',
              }}
            >
              <div className="text-sm font-semibold" style={{ color: BRACKET_COLORS[b] }}>B{b} · {BRACKET_LABELS[b]}</div>
              <div className="text-xs text-on-surface-variant">{BRACKET_BLURBS[b]}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Step 3 — themes */}
      <section>
        <div className="text-base text-on-surface-variant mb-0.5">3. Themes <span className="opacity-70">(EDHREC, pick any)</span></div>
        {!commander && <div className="text-sm text-on-surface-variant">Choose a commander first.</div>}
        {commander && themesQuery.isPending && <div className="text-sm text-on-surface-variant">Loading themes…</div>}
        {commander && themesQuery.isError && (
          <div className="text-sm text-error-text">{themesQuery.error instanceof Error ? themesQuery.error.message : 'Failed to load themes'}</div>
        )}
        {themesQuery.data && themesQuery.data.themes.length === 0 && (
          <div className="text-sm text-on-surface-variant">EDHREC lists no themes for this commander — you can still build by role.</div>
        )}
        {themesQuery.data && themesQuery.data.themes.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {themesQuery.data.themes.map((t) => {
              const on = themes.has(t.slug);
              return (
                <button
                  key={t.slug}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleTheme(t.slug)}
                  className="rounded-pill border px-2.5 py-1 text-sm cursor-pointer"
                  style={{
                    borderColor: on ? 'var(--primary)' : 'var(--outline-variant)',
                    background: on ? 'var(--primary)' : 'var(--surface-container)',
                    color: on ? 'var(--on-primary)' : 'var(--on-surface)',
                  }}
                  title={`${t.count.toLocaleString()} decks${t.is_typal ? ' · typal' : ''}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        )}
      </section>

      <div>
        <label htmlFor="guided-deck-name" className="text-base text-on-surface-variant mb-0.5">Deck name</label>
        <input id="guided-deck-name" type="text" className="input-field" value={deckName} onChange={(e) => setDeckName(e.target.value)} placeholder={commander || 'Defaults to the commander'} />
      </div>

      {error && <div className="text-error-text p-3 bg-status-error-bg rounded-sm text-base">{error}</div>}

      <div className="flex gap-2 justify-end mt-1">
        <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
        <button
          type="button"
          onClick={() => void create()}
          disabled={!commander || creating || themesQuery.isPending || themesQuery.isError}
          className="btn-primary"
          style={{
            opacity: !commander || creating || themesQuery.isPending || themesQuery.isError ? 0.7 : 1,
            cursor: !commander || creating || themesQuery.isPending || themesQuery.isError ? 'not-allowed' : 'pointer',
          }}
        >
          {creating ? 'Creating…' : 'Create deck'}
        </button>
      </div>
    </div>
  );
}
